import React, { useContext } from 'react';
import {
    HiOutlineXMark, HiOutlineCog6Tooth,
    HiOutlineQueueList, HiOutlineVariable, HiOutlinePlus,
} from 'react-icons/hi2';

import Section from './section';
import PlaybookSettings from './playbook-settings';
import { PlaybookContext } from '../context/PlaybookContext';

/**
 * The steps list: header, the sections, and the icon rail down the right.
 *
 * The rail switches which editor occupies the side panel - the step list itself
 * or the app's variables - mirroring the mock.
 */

export const PANEL = {
    STEPS: 'STEPS',
    SETTINGS: 'SETTINGS',
    VARIABLES: 'VARIABLES',
};

function RailButton({ active, onClick, title, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            aria-label={title}
            className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-all
                ${active
                    ? 'border-gray-300 bg-gray-100 text-primary'
                    : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300 hover:text-gray-600'}`}
        >
            {children}
        </button>
    );
}

export default function StepsPanel({ panel, onPanelChange, onClose, lookups, slotOptions, onCreateVariable }) {
    const { playbook, addSection, compileErrors, compileWarnings } = useContext(PlaybookContext);

    // Warnings read the same way here: neither is tied to a particular step.
    const generalErrors = [...compileErrors, ...(compileWarnings || [])].filter(e => !e.stepId);

    return (
        // h-full so the list below gets a bounded height to scroll inside; without
        // it max-h-full resolves against nothing and the panel grows unbounded.
        <div className="flex h-full gap-3">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <header className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-3.5">
                    <div><h2 className="text-lg font-semibold tracking-tight text-gray-900">Steps</h2><p className="mt-1 text-xs text-gray-500">Guide your agent with clear instructions, one step at a time.</p></div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        aria-label="Close"
                    >
                        <HiOutlineXMark size={20} />
                    </button>
                </header>

                {generalErrors.length > 0 && (
                    <div className="shrink-0 border-b border-amber-100 bg-amber-50 px-5 py-2.5 text-[13px] text-amber-800">
                        {generalErrors.map((e, i) => <div key={i}>{e.message}</div>)}
                    </div>
                )}

                <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50/60 p-3 sm:p-5">
                    {(playbook?.sections || []).map((section, i) => (
                        <Section
                            key={section.id}
                            section={section}
                            index={i + 1}
                            lookups={lookups}
                            slotOptions={slotOptions}
                            onCreateVariable={onCreateVariable}
                        />
                    ))}

                    <div className="px-5 py-4">
                        <button
                            type="button"
                            onClick={addSection}
                            className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-medium text-primary transition-colors hover:bg-gray-100"
                        >
                            <HiOutlinePlus size={14} /> Add section
                        </button>
                    </div>
                </div>
            </div>

            {panel === PANEL.SETTINGS && <PlaybookSettings onClose={() => onPanelChange(PANEL.STEPS)} />}

            <div className="flex shrink-0 flex-col gap-2">
                <RailButton title="Playbook settings" active={panel === PANEL.SETTINGS} onClick={() => onPanelChange(PANEL.SETTINGS)}>
                    <HiOutlineCog6Tooth size={18} />
                </RailButton>
                <RailButton
                    title="Steps"
                    active={panel === PANEL.STEPS}
                    onClick={() => onPanelChange(PANEL.STEPS)}
                >
                    <HiOutlineQueueList size={18} />
                </RailButton>
                <RailButton
                    title="Variables"
                    active={panel === PANEL.VARIABLES}
                    onClick={() => onPanelChange(PANEL.VARIABLES)}
                >
                    <HiOutlineVariable size={18} />
                </RailButton>
            </div>
        </div>
    );
}
